import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Alert,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axiosInstance from "../../lib/axios";
import DashboardLayout from "../../components/dashboard/DashboardLayout";

const ImportDataPage = () => {
  const [phase, setPhase] = useState("idle"); 
  // idle | uploading | imported | analyzing | done

  const [file, setFile] = useState(null);
  const [importType, setImportType] = useState("products");
  const [loading, setLoading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;

    try {
      setLoading(true);
      setPhase("uploading");

      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", importType);

      const res = await axiosInstance.post("/import", formData);

      if (res.status === 200) {
        setPhase("imported");
      }
    } catch (err) {
      console.error(err);
      setPhase("idle");
    } finally {
      setLoading(false);
    }
  };

  const handleStartAnalytics = async () => {
    try {
      setLoading(true);
      setPhase("analyzing");

      const res = await axiosInstance.get("/analytics/refresh");

      if (res.status === 200) {
        setPhase("done");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
   <DashboardLayout>
         <Box
        sx={{
            minHeight: "80vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `
            radial-gradient(circle at 20% 20%, rgba(27,58,45,0.08), transparent 40%),
            radial-gradient(circle at 80% 80%, rgba(196,151,90,0.06), transparent 40%)
            `,
        }}
        >
        <Paper
            sx={{
            width: 600,
            borderRadius: "18px",
            background: "var(--color-background-primary)",
            border: "1px solid rgba(255,255,255,0.06)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            overflow: "hidden",
            }}
        >
            {/* Header */}
            <Box sx={{ p: 3 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                Import inventory data
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "var(--color-text-secondary)" }}>
                Upload → Validate → Analyze your business data
            </Typography>
            </Box>

            <Box sx={{ px: 3, pb: 3 }}>
            {/* IDLE */}
            {phase === "idle" && (
                <>
                <TextField
                    select
                    fullWidth
                    label="Import type"
                    value={importType}
                    onChange={(e) => setImportType(e.target.value)}
                    sx={{ mb: 2 }}
                >
                    <MenuItem value="products">Products</MenuItem>
                    <MenuItem value="inventory">Inventory</MenuItem>
                    <MenuItem value="variants">Variants</MenuItem>
                    <MenuItem value="sales">Sales</MenuItem>
                </TextField>

                {/* Upload Box */}
                <Box
                    component="label"
                    sx={{
                    display: "block",
                    borderRadius: "14px",
                    p: 5,
                    cursor: "pointer",
                    textAlign: "center",
                    background:
                        "linear-gradient(180deg, rgba(27,58,45,0.08), rgba(0,0,0,0.2))",
                    border: "1px dashed rgba(255,255,255,0.12)",
                    "&:hover": {
                        transform: "translateY(-3px)",
                    },
                    }}
                >
                    <UploadFileIcon sx={{ fontSize: 30, color: "#C4975A" }} />
                    <Typography mt={1}>
                    {file ? file.name : "Click to select file"}
                    </Typography>

                    <input
                    hidden
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                </Box>

                {file && (
                    <Alert sx={{ mt: 2 }}>
                    Selected file: {file.name}
                    </Alert>
                )}

                <Button
                    fullWidth
                    onClick={handleUpload}
                    disabled={!file || loading}
                    sx={{
                    mt: 3,
                    background: "#1B3A2D",
                    color: "#E8F0EB !important",
                    "&:hover": { background: "#2A5040" },
                    }}
                >
                    {loading ? "Uploading..." : "Start Import"}
                </Button>
                </>
            )}

            {/* UPLOADING */}
            {phase === "uploading" && (
                <Box textAlign="center" py={4}>
                <CircularProgress sx={{ color: "#BA7517", mb: 2 }} />
                <Typography>Uploading & validating...</Typography>
                </Box>
            )}

            {/* IMPORTED */}
            {phase === "imported" && (
                <Box textAlign="center" py={4}>
                <CheckCircleIcon sx={{ fontSize: 48, color: "#639922" }} />
                <Typography mt={1} fontWeight={500}>
                    Import completed
                </Typography>

                <Button
                    onClick={handleStartAnalytics}
                    sx={{
                    mt: 3,
                    background: "#1B3A2D",
                    color: "#E8F0EB",
                    }}
                >
                    Run Analysis
                </Button>
                </Box>
            )}

            {/* ANALYZING */}
            {phase === "analyzing" && (
                <Box textAlign="center" py={4}>
                <CircularProgress sx={{ color: "#1B3A2D", mb: 2 }} />
                <Typography>Running analytics...</Typography>
                </Box>
            )}

            {/* DONE */}
            {phase === "done" && (
                <Box textAlign="center" py={4}>
                <CheckCircleIcon sx={{ fontSize: 48, color: "#639922" }} />
                <Typography mt={1} fontWeight={500}>
                    Analysis ready 🎉
                </Typography>
                </Box>
            )}
            </Box>
        </Paper>
        </Box>
   </DashboardLayout>
  );
};

export default ImportDataPage;